import { Icon, styled, Tooltip } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

type ITagStatus = 'idle' | 'loading' | 'matched' | 'unmatched';

const ICON_MAP = {
  idle: null,
  loading: null,
  matched: (
    <Icon
      icon="check"
      css={{ color: '$palette-indigo-500', fontSize: '14px' }}
    />
  ),
  unmatched: (
    <Icon
      icon="warning"
      css={{ color: '$palette-red-500', fontSize: '14px' }}
    />
  ),
};

const VARIANT_MAP: Record<ITagStatus, 'base' | 'matched' | 'unmatched'> = {
  idle: 'base',
  loading: 'base',
  matched: 'matched',
  unmatched: 'unmatched',
};

const RemoveButton = styled('button', {
  padding: '2px',
  lineHeight: '8px',
  display: 'inline-flex',
  alignItems: 'center',
  margin: 0,
  border: 'none',
  fontSize: '12px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
});

const TagContainer = styled('div', {
  alignItems: 'center',
  backgroundColor: '$palette-grey-100',
  borderRadius: '$small',
  display: 'flex',
  fontSize: '14px',
  gap: '2px',
  lineHeight: '18px',
  maxWidth: '100%',
  padding: '4px',
  width: 'fit-content',
  variants: {
    status: {
      base: {
        backgroundColor: '$palette-grey-100',
      },
      unmatched: {
        backgroundColor: '$palette-red-50',
      },
      matched: {
        backgroundColor: '$palette-indigo-50',
      },
    },
  },
});

interface ITag {
  title: string;
  onRemove?: () => void;
  status?: ITagStatus;
}

export default function Tag({ title, onRemove, status = 'loading' }: ITag) {
  const { t__react } = useTranslation();
  const content = (
    <TagContainer status={VARIANT_MAP[status]} data-testid="tag">
      {ICON_MAP[status]}
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </span>
      <RemoveButton onClick={onRemove}>
        <Icon icon="close" css={{ fontSize: 'inherit' }} />
      </RemoveButton>
    </TagContainer>
  );

  return status === 'unmatched' ? (
    <Tooltip
      title={t__react('core.unmatchedTerms', {
        term: <b>{title}</b>,
      })}
    >
      {content}
    </Tooltip>
  ) : (
    content
  );
}
